import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Immutable, produce } from 'immer';
import { ProfileState } from '../../../../profile/store/profile.state';
import { DEFAULT_TIME_UNITS_FORM, TIME_UNITS } from '../../../../shared/constants/time-units.constant';
import { NavigateService } from '../../../../shared/services/navigate.service';
import { AuthState } from '../../../../shared/store/auth.state';
import { NgxsStateFacade } from '../../../../shared/store/ngxs-state-facade';
import { Machine } from '../models/machine.model';
import { StartMachine } from '../models/start-machine';
import { MachineUtilsService } from '../services/machine-utils.service';
import { MachineService } from '../services/machine.service';

export interface MachineStateModel {
  machine: Machine;
  canCancel: boolean;
  timeUnits: Array<number>;
  timeUnitsForm: { model: { timeUnits: number }; dirty: boolean; status: string; errors: { [key: string]: unknown } };
  isFlowInProgress: boolean;
  isPending: boolean;
  isTimeAdded: boolean;
}

export class SetCanCancel {
  static readonly type = '[Machine] Set Can Cancel';
  constructor(public canCancel: boolean) {}
}

export class MarkFlowCompleted {
  static readonly type = '[Machine] Mark Flow Completed';
}

export class MarkFlowStarted {
  static readonly type = '[Machine] Mark Flow Started';
}

export class StartFlow {
  static readonly type = '[Machine] Start Flow';
  constructor(public machine: Machine) {}
}

export class StartAddTimeFlow {
  static readonly type = '[Machine] Start Add Time Flow';
  constructor(public machine: Machine) {}
}

export class CancelFlow {
  static readonly type = '[Machine] Cancel Flow';
}

export class CompleteFlow {
  static readonly type = '[Machine] Complete Flow';
}

export class SetMachine {
  static readonly type = '[Machine] Set Machine';
  constructor(public machine: Machine) {}
}

export class StartMachineAction {
  static readonly type = '[Machine] Start Machine';
}

@State<MachineStateModel>({
  name: 'machine',
  defaults: {
    canCancel: true,
    machine: undefined,
    timeUnits: TIME_UNITS,
    timeUnitsForm: DEFAULT_TIME_UNITS_FORM,
    isFlowInProgress: false,
    isPending: false,
    isTimeAdded: false,
  },
})
@Injectable()
export class MachineState extends NgxsStateFacade<MachineStateModel> {
  constructor(
    store: Store,
    private readonly auth: AuthState,
    private readonly machineSvc: MachineService,
    private readonly navigate: NavigateService,
    private readonly profile: ProfileState,
  ) {
    super(store, 'machine');
  }

  @Selector()
  static canCancel(state: MachineStateModel): boolean {
    return state.canCancel;
  }

  @Selector()
  static machine(state: MachineStateModel): Machine {
    return state.machine;
  }

  @Selector()
  static isFlowInProgress(state: MachineStateModel): boolean {
    return state.isFlowInProgress;
  }

  @Selector()
  static isPending(state: MachineStateModel): boolean {
    return state.isPending;
  }

  @Selector()
  static isTimeAdded(state: MachineStateModel): boolean {
    return state.isTimeAdded;
  }

  // --- Public API ---

  get canCancel(): boolean {
    return this.store.selectSnapshot(MachineState.canCancel);
  }

  get canAddTime(): boolean {
    return (this.isDryer || this.isIroningRoll) && this.isOurs;
  }

  get machine(): Machine {
    return this.store.selectSnapshot(MachineState.machine);
  }

  get priceOfAction(): number {
    const state = this.snapshot;
    return state.timeUnitsForm?.model?.timeUnits * this.machine.pricePerUnit;
  }

  get timeMultiples(): Immutable<Array<number>> {
    return this.snapshot.timeUnits;
  }

  get timeUnitSize(): number {
    return this.machine?.unit;
  }

  get isAvailable(): boolean {
    return MachineUtilsService.isAvailable(this.machine);
  }

  get isBusy(): boolean {
    return MachineUtilsService.isBusy(this.machine);
  }

  get isDryer(): boolean {
    return MachineUtilsService.isDryer(this.machine);
  }

  get isIroningRoll(): boolean {
    return MachineUtilsService.isIroningRoll(this.machine);
  }

  get isOurs(): boolean {
    return MachineUtilsService.isOurs(this.machine, this.profile.currentUserId);
  }

  get isFlowInProgress(): boolean {
    return this.store.selectSnapshot(MachineState.isFlowInProgress);
  }

  get isPending(): boolean {
    return this.store.selectSnapshot(MachineState.isPending);
  }

  get isMachineStartDisabled(): boolean {
    return this.isPending || !this.isAvailable;
  }

  get isTimeAdded(): boolean {
    return this.store.selectSnapshot(MachineState.isTimeAdded);
  }

  get isWasher(): boolean {
    return MachineUtilsService.isWashingMachine(this.machine);
  }

  setCanCancel(canCancel: boolean): void {
    this.store.dispatch(new SetCanCancel(canCancel));
  }

  markFlowCompleted(): void {
    this.store.dispatch(new MarkFlowCompleted());
  }

  markFlowStarted(): void {
    this.store.dispatch(new MarkFlowStarted());
  }

  startFlow(machine: Machine): void {
    this.store.dispatch(new StartFlow(machine));
  }

  startAddTimeFlow(machine: Machine): void {
    this.store.dispatch(new StartAddTimeFlow(machine));
  }

  cancelFlow(): void {
    this.store.dispatch(new CancelFlow());
  }

  completeFlow(): void {
    this.store.dispatch(new CompleteFlow());
  }

  setMachine(machine: Machine): void {
    this.store.dispatch(new SetMachine(machine));
  }

  startMachine(): void {
    this.store.dispatch(new StartMachineAction());
  }

  // --- Action Handlers ---

  @Action(SetCanCancel)
  setCanCancelHandler(ctx: StateContext<MachineStateModel>, action: SetCanCancel) {
    ctx.patchState({ canCancel: action.canCancel });
  }

  @Action(MarkFlowCompleted)
  markFlowCompletedHandler(ctx: StateContext<MachineStateModel>) {
    ctx.patchState({ isFlowInProgress: false });
  }

  @Action(MarkFlowStarted)
  markFlowStartedHandler(ctx: StateContext<MachineStateModel>) {
    ctx.patchState({ isFlowInProgress: true });
  }

  @Action(StartFlow)
  startFlowHandler(ctx: StateContext<MachineStateModel>, action: StartFlow) {
    ctx.patchState({ isFlowInProgress: true });
    const machine = action.machine;
    if (
      machine &&
      (MachineUtilsService.isAvailable(machine) || MachineUtilsService.isOurs(machine, this.profile.currentUserId))
    ) {
      if (machine.multiProgram) {
        this.navigate.toMachineProgramSelect(machine.locationId, machine.number);
      } else {
        this.navigate.toMachineDetail(machine.locationId, machine.number);
      }
    }
  }

  @Action(StartAddTimeFlow)
  startAddTimeFlowHandler(ctx: StateContext<MachineStateModel>, action: StartAddTimeFlow) {
    const machine = action.machine;
    if (MachineUtilsService.isDryer(machine) || MachineUtilsService.isIroningRoll(machine)) {
      ctx.patchState({ isFlowInProgress: true });
      this.navigate.toMachineAddTime(machine.locationId, machine.number);
    }
  }

  @Action(CancelFlow)
  cancelFlowHandler(ctx: StateContext<MachineStateModel>) {
    this.clearCurrentFlow(ctx);
    const machine = this.machine;
    if (machine.customerId === this.profile.currentUserId) {
      this.navigate.toLocationById(machine.locationId);
    } else {
      this.navigate.toLocationMachines(machine.locationId, machine.type);
    }
  }

  @Action(CompleteFlow)
  completeFlowHandler(ctx: StateContext<MachineStateModel>) {
    this.clearCurrentFlow(ctx);
    this.navigate.toLocationById(this.machine.locationId);
  }

  @Action(SetMachine)
  setMachineHandler(ctx: StateContext<MachineStateModel>, action: SetMachine) {
    ctx.patchState({ machine: action.machine });
  }

  @Action(StartMachineAction)
  startMachineHandler(ctx: StateContext<MachineStateModel>) {
    ctx.patchState({ isPending: true });

    const machine = this.machine;
    const timeAdded = this.isBusy;

    const startMachine: StartMachine = {
      number: machine.number,
      pricePerUnit: machine.pricePerUnit,
      unit: machine.unit,
      amountOfUnits: ctx.getState().timeUnitsForm.model.timeUnits,
      customerId: this.profile.currentUserId,
    };

    return this.machineSvc.startMachine(machine.locationId, startMachine).subscribe(
      (updated: Machine): void => {
        this.auth.triggerRefresh();
        ctx.setState(
          produce(ctx.getState(), (draft) => {
            draft.machine = updated;
            draft.isTimeAdded = timeAdded;
            draft.isPending = false;
          }),
        );
        this.navigate.toMachineStartSuccess(machine.locationId, machine.number);
      },
      (err: HttpErrorResponse) => {
        ctx.patchState({ isPending: false });
        if (err.status === 402) {
          this.navigate.toMachineStartInsufficientFunds(machine.locationId, machine.number);
          return;
        }
        this.navigate.toMachineStartFailure(machine.locationId, machine.number);
      },
    );
  }

  private clearCurrentFlow(ctx: StateContext<MachineStateModel>) {
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        draft.timeUnitsForm.model.timeUnits = 1;
        draft.isFlowInProgress = false;
      }),
    );
  }
}
